import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { listBetaCycles, upsertBetaCycle } from "@/lib/beta.functions";
import { PageHeader } from "@/components/admin/AdminPrimitives";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/beta/ciclos/")({
  head: () => ({
    meta: [
      { title: "Ciclos de Beta" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CyclesPage,
});

const STATUS_LABELS: Record<string, string> = {
  planejado: "Planejado",
  em_andamento: "Em andamento",
  pausado: "Pausado",
  encerrado: "Encerrado",
  cancelado: "Cancelado",
};

function CyclesPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listBetaCycles);
  const upsertFn = useServerFn(upsertBetaCycle);
  const [showNew, setShowNew] = useState(false);

  const list = useQuery({ queryKey: ["beta-cycles"], queryFn: () => listFn() });

  const create = useMutation({
    mutationFn: (payload: any) => upsertFn({ data: payload }),
    onSuccess: () => {
      toast.success("Ciclo criado.");
      setShowNew(false);
      qc.invalidateQueries({ queryKey: ["beta-cycles"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Falha ao criar."),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ciclos de Beta"
        description="Registre e acompanhe cada ciclo do beta controlado."
        actions={
          <button
            onClick={() => setShowNew((s) => !s)}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white"
          >
            <Plus size={14} /> Novo ciclo
          </button>
        }
      />

      {showNew && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            create.mutate({
              name: String(fd.get("name") ?? ""),
              starts_on: String(fd.get("starts_on") ?? ""),
              ends_on: String(fd.get("ends_on") ?? "") || null,
              max_participants: Number(fd.get("max_participants") ?? 30),
              status: "planejado",
              notes: String(fd.get("notes") ?? "") || null,
            });
          }}
          className="grid gap-3 rounded-xl border border-black/[0.06] bg-white p-5 text-sm md:grid-cols-2"
        >
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Nome</span>
            <input name="name" required className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5" />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Máx. participantes</span>
            <input name="max_participants" type="number" defaultValue={30} min={1} className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5" />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Início</span>
            <input name="starts_on" type="date" required className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5" />
          </label>
          <label className="block">
            <span className="text-xs text-[var(--gray-ink)]">Encerramento previsto</span>
            <input name="ends_on" type="date" className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs text-[var(--gray-ink)]">Observações</span>
            <textarea name="notes" rows={2} className="mt-1 w-full rounded-md border border-black/10 px-2 py-1.5" />
          </label>
          <div className="md:col-span-2">
            <button
              disabled={create.isPending}
              className="rounded-md bg-[var(--navy)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              Salvar ciclo
            </button>
          </div>
        </form>
      )}

      <section className="rounded-xl border border-black/[0.06] bg-white">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.14em] text-[var(--gray-ink)]/70">
            <tr>
              <th className="px-4 py-3">Ciclo</th>
              <th className="px-4 py-3">Início</th>
              <th className="px-4 py-3">Encerramento</th>
              <th className="px-4 py-3">Máx.</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {(list.data?.items ?? []).map((c: any) => (
              <tr key={c.id} className="border-t border-black/5">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-xs">{c.starts_on}</td>
                <td className="px-4 py-3 text-xs">{c.ends_on ?? "—"}</td>
                <td className="px-4 py-3 text-xs">{c.max_participants}</td>
                <td className="px-4 py-3 text-xs">{STATUS_LABELS[c.status] ?? c.status}</td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to="/admin/beta/ciclos/$cycleId"
                    params={{ cycleId: c.id }}
                    className="text-xs font-semibold text-[var(--navy)] hover:underline"
                  >
                    Abrir
                  </Link>
                </td>
              </tr>
            ))}
            {list.data?.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-[var(--gray-ink)]">
                  Nenhum ciclo registrado. Crie o primeiro para começar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
